import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable/Clickable'
import clsx from 'clsx'
import { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'

const Container = styled.div`
  padding: ${rem(12)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  width: 100%;

  ${mediaQueryUp.sm} {
    width: ${rem(300)};
  }
`

const ClickableSelect = styled(Clickable)`
  --border-colour: ${({ theme }) => theme.palette.neutral.five};
  --bg-colour: ${({ theme }) => theme.palette.neutral.eight};

  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: ${rem(8)};
  align-items: center;
  padding: ${rem(8)};
  border: 1px solid var(--border-colour);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background-color: var(--bg-colour);

  &.selected {
    --border-colour: ${({ theme }) => theme.palette.neutral.one};
  }

  &.error {
    --border-colour: ${({ theme }) => theme.palette.messaging.critical.main};
    --bg-colour: ${({ theme }) => theme.palette.messaging.critical.light};
  }
`

interface Props {
  hotelNumber: number;
  bundleOffer: BundledOfferState
  onClick: (e: MouseEvent<HTMLButtonElement>, offerId?: string) => void;
  onClickLabel: string
  isSelected: boolean
  isDatePickerOpen: boolean
  noSelections: boolean
}

export default function BundleAndSaveDateSelectionTrigger({
  hotelNumber,
  bundleOffer,
  onClick,
  onClickLabel,
  isSelected,
  isDatePickerOpen,
  noSelections,
}: Props) {
  const {
    checkInDate,
    checkOutDate,
    duration,
    offer,
  } = bundleOffer

  const [isDirty, setDirty] = useState(false)

  useEffect(() => {
    if (isDatePickerOpen) {
      setDirty(false)
    }
  }, [isDatePickerOpen])

  useEffect(() => {
    if (!bundleOffer.checkInDate && !isDatePickerOpen && !noSelections) {
      setDirty(true)
    }
  }, [bundleOffer, isDatePickerOpen, noSelections])

  const dateLabel = useMemo(() => {
    if (checkInDate && duration) {
      return `${checkInDate.format('DD MMM')} - ${checkInDate.clone().add(duration, 'days').format(
      'DD MMM',
    )} (${duration} nights)`
    }

    if (checkInDate && checkOutDate) {
      return `${checkInDate.format('DD MMM')} - ${checkOutDate.format(
        'DD MMM',
      )} (${checkOutDate.diff(checkInDate, 'days')} nights)`
    }

    return null
  }, [checkInDate, checkOutDate, duration])

  const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    onClick(e, offer?.id)
  }, [onClick, offer])

  return (
    <Container>
      <div>
        <BodyText variant="small">Hotel {hotelNumber}</BodyText>
        <BodyText lineClamp={1} variant="medium" weight="bold">
          {offer?.property?.name}
        </BodyText>
      </div>
      <ClickableSelect
        onClick={handleClick}
        className={clsx({
          selected: isSelected && isDatePickerOpen,
          error: isDirty,
        })}
      >
        <LineCalendarIcon />
        <BodyText variant="medium">{dateLabel ?? onClickLabel}</BodyText>
        <DropdownChevron open={isSelected && isDatePickerOpen} />
      </ClickableSelect>
    </Container>
  )
}
